/**
 * A module representing user answers.
 * @module user-answers
 * @exports UserAnswers
 * @see UserAnswers
 * @requires module:helpers
 * @requires module:training
 * @example
 * var UserAnswers = require('user-answers');
 */

/**
 * @typedef {Object} QuizPageProgress
 * @property {Array} childrenID - Quiz page children entities ID
 * @property {Array} correct - Quiz page user answers status
 * @property {Boolean} inProgress - is Quiz page in progress
 * @property {Boolean} isAnswered - is Quiz page answered
 * @property {Boolean} isPassed - is Quiz page passed
 * @property {Boolean} isVisited - is Quiz page visited
 * @property {Array} progress - Quiz page user answers progress status
 * @property {Integer} userScore - user score in Quiz page
 */

define('user-answers',['helpers', 'training', 'get-tasks-answers', 'get-answer-prop'], function(helpers, Training, getTasksAnswers, getAnswerProp) {
    'use strict';

    var userAnswers,
        training;

    function flattenChildren(entities) {
        var res = [],
            flatten = function(arr) {
                for (var i = 0; i < arr.length; i++) {
                    res.push(arr[i]);

                    if (arr[i].children && arr[i].children.length) {
                        flatten(arr[i].children);
                    }
                }
            };

        flatten(entities);

        return res;
    }

    function isSingleChoiceCorrect(answers, userAnswers) {
        var i = answers.length,
            userAnswer = (Array.isArray(userAnswers) && userAnswers.length) ? userAnswers[0] : {},
            userAnswerID = userAnswer.answerId || '',
            isCorrect = false;

        while(i--) {
            if (answers[i].hasOwnProperty('points') && answers[i].points && answers[i].points > 0) {
                if (userAnswerID === answers[i].id) {
                    isCorrect = true;
                }
            }
        }

        return isCorrect;
    }

    function isMultipleChoiceCorrect(answers, userAnswers) {
        var i = answers.length,
            j = 0,
            totalUserAnswers = userAnswers.length,
            totalCorrect = 0,
            checkedCorrect = 0;

        while(i--) {
            if (answers[i].hasOwnProperty('points') && answers[i].points && answers[i].points > 0) {
                j = 0;
                while(j < totalUserAnswers) {
                    if (userAnswers[j].answerId === answers[i].id) {
                        checkedCorrect++;
                        break;
                    }
                    j++;
                }
                totalCorrect++;
            }
        }

        return (totalCorrect === checkedCorrect && checkedCorrect === totalUserAnswers) ? true : false;
    }

    function getYesNoTotalCorrect(questions, userAnswers) {
        var total = 0,
            arr;

        userAnswers.forEach(function(userAnswer) {
            arr = questions.filter(function(question) {
                return (userAnswer.questionId === question.id && userAnswer.answerId === question.correctAnswerId);
            });
            total += (arr.length) ? 1 : 0;
        });

        return total;
    }

    function isYesNoCorrect(questions, userAnswers) {
        var total = getYesNoTotalCorrect(questions, userAnswers);

        return (total === questions.length && total === userAnswers.length) ? true : false;
    }

    function isFillInBlanksCorrect(answers, userAnswers) {
        var i = answers.length,
            j = 0,
            totalUserAnswers = userAnswers.length,
            totalCorrect = 0,
            checkedCorrect = 0,
            requiredAnswers = [];

        while (i--) {

            if (requiredAnswers.indexOf(answers[i].questionId) === -1) {
                requiredAnswers.push(answers[i].questionId);
            }

            if (answers[i].points > 0) {
                j = 0;
                while(j < totalUserAnswers) {
                    if ( userAnswers[j].answerId === answers[i].id ) {
                        checkedCorrect++;
                        break;
                    }
                    j++;
                }
                totalCorrect++;
            }
        }

        return ((totalCorrect === checkedCorrect || checkedCorrect === requiredAnswers.length) && totalCorrect >= requiredAnswers.length) ? true : false;
    }

    function getSortTotalCorrect(answers, userAnswers) {
        var total = 0,
            arr;

        userAnswers.forEach(function(userAnswer) {
            arr = answers.filter(function(answer) {
                return (userAnswer.answerId === answer.id && userAnswer.questionId === answer.questionId);
            });
            total += (arr.length) ? 1 : 0;
        });

        return total;
    }

    function isSortCorrect(answers, userAnswers) {
        var total = getSortTotalCorrect(answers, userAnswers);

        return (total === userAnswers.length) ? true : false;
    }

    function getEntityScore(entity, userAnswers) {
        var score = 0,
            arr,
            answers = (entity && entity.answers) ? entity.answers : [],
            questions = (entity && entity.questions) ? entity.questions : [];

        userAnswers = userAnswers || [];

        switch(entity.typeId) {
        case 14:                        // Yes No
        case 15:                        // Set in order
        case 16:                        // Martch pair
            score = getYesNoTotalCorrect(questions, userAnswers);
            break;
        case 20:                        // Sort to groups
            score = getSortTotalCorrect(answers, userAnswers);
            break;
        default:
            userAnswers.forEach(function(userAnswer) {
                arr = answers.filter(function(answer) {
                    return (answer.id === userAnswer.answerId);
                });
                score += (arr.length) ? arr[0].points : 0;
            });
        }

        return score;
    }

    /**
     * @constructor
     * @alias UserAnswers
     * @param {Object} options
     * @param {Array} [options.answers] - User answers
     */
    function UserAnswers(options) {
        options = options || {};

        /** @name UserAnswers#answers - User answers array */
        this.answers = Array.isArray(options.answers) ? options.answers : [];

        this.parser = function(children) {
            return flattenChildren(children).slice(0);
        }
    }

    /**
     * Sets user answers.
     * @param {Array} answers - user answers
     */
    UserAnswers.prototype.setUserAnswers = function(answers) {
        if (Array.isArray(answers)) {
            this.answers = answers;
        }
    };

    /**
     * Returns user answer by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Object}
     */
    UserAnswers.prototype.getUserAnswer = function(ID) {
        var answer,
            answers = this.answers;

        for (var i = 0; i < answers.length; i++) {
            answer = this.answers[i];

            if (answer.contentEntityId && answer.contentEntityId === ID) {
                return answer;
            }
        }

        return {};
    };

    /**
     * Returns user answer answers by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Array}
     */
    UserAnswers.prototype.getUserAnswers = function(ID) {
        var answer = this.getUserAnswer(ID);

        return answer.answers || [];
    };

    /**
     * Adds user answer by entity ID.
     * @param {String} ID - Entity ID
     * @param {Object} answer - Answer to add
     * @returns {Object}
     */
    UserAnswers.prototype.addAnswer = function(ID, answer) {
        var userAnswer = this.getUserAnswer(ID);

        if (userAnswer.answers && Object.keys(answer).length) {
            userAnswer.answers.push(answer);
        }
    }

    /**
     * Updates user answer by entity ID and returns it.
     * @param {String} ID - Entity ID
     * @param {Object} props - properties to update, key value pairs
     * @returns {Object}
     */
    UserAnswers.prototype.logProgress = function(ID, props) {
        var userAnswer = this.getUserAnswer(ID);

        if (userAnswer) {
            for (var prop in props) {
                userAnswer[prop] = props[prop];
            }
        }

        return userAnswer;
    };

    /**
     * Sets entity to visited
     * @param {String} ID - Entity ID
     */
    UserAnswers.prototype.logVisited = function(ID) {
        if (!this.isPageVisited(ID)) {
            this.logProgress(ID, {visited: true});
        }
    }

    /**
     * Checks if entity is visited by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isPageVisited = function(ID) {
        var userAnswer = this.getUserAnswer(ID);

        return (userAnswer && userAnswer.visited) ? true : false;
    };

    /**
     * Checks if entity and it's children are visited by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isSectionVisited = function(ID) {
        var entity = training.getEntity(ID);

        if (!entity) {
            return false;
        }

        if (helpers.entity.isQuizPage(entity)) {
            return this.isQuizPagePassed(ID);
        }

        if (helpers.entity.isContentPage(entity) && !this.isPageVisited(ID) || helpers.entity.isTaskPage(entity) && (training.sequence || entity.contentEntity.requiresAnswer) && !userAnswers.isTaskPageAnswered(ID)) {
            return false;
        }

        if (Array.isArray(entity.children) && entity.children.length) {
            for (var i = 0; i < entity.children.length; i++) {
                if (!this.isSectionVisited(entity.children[i].contentEntityId)) {
                    return false;
                }
            }
        }

        return true;
    };

    /**
     * Checks if task is public by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isAnswerPublic = function(ID) {
        var userAnswer = this.getUserAnswer(ID),
            answers = (userAnswer && Array.isArray(userAnswer.answers)) ? userAnswer.answers : [];

        return answers.filter(function(answer) {
            return answer.isPublic;
        }).length ? true : false;
    };

    /**
     * Checks if task is answered by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isTaskPageAnswered = function(ID) {
        var userAnswer = this.getUserAnswer(ID);

        if (training.scormPackage) {
            return this.isPageVisited(ID);
        }

        return (userAnswer && Array.isArray(userAnswer.answers) && userAnswer.answers.length) ? true : false;
    };

    /**
     * Checks if entity is correct.
     * @param {Object} entity - Entity object
     * @returns {Boolean}
     */
    UserAnswers.prototype.isEntityCorrect = function(entity) {
        var ID = (entity) ? entity.contentEntityId : '',
            userAnswer = this.getUserAnswer(ID),
            typeID = (entity) ? entity.typeId : 0,
            displayMode = (entity) ? entity.contentEntity.displayMode : 0;

        if (!entity || !entity.answers || !userAnswer || !userAnswer.answers) {
            return false;
        }

        switch(typeID) {
        case 10:                                                                                            // Content page
            return true;
        case 12:                                                                                            // Open question
            return true;
        case 13:                                                                                            // Multiple choice
            switch(displayMode) {
            case 3:                                                                                             // Checkboxes
                return isMultipleChoiceCorrect(entity.answers, userAnswer.answers);
            default:                                                                                            // Radio & Push buttons
                return isSingleChoiceCorrect(entity.answers, userAnswer.answers);
            }
        case 14:                                                                                            // Yes No
            return isYesNoCorrect(entity.questions, userAnswer.answers);
        case 15:                                                                                            // Set in order
            return isYesNoCorrect(entity.questions, userAnswer.answers);
        case 16:                                                                                            // Martch pair
            return isYesNoCorrect(entity.questions, userAnswer.answers);
        case 17:                                                                                            // Evaluate
            return true;
        case 18:                                                                                            // Fill in blanks
            return isFillInBlanksCorrect(entity.answers, userAnswer.answers);
        case 20:                                                                                            // Sort to groups
            return isSortCorrect(entity.answers, userAnswer.answers);
        default:
            return false;
        }
    };

    /**
     * Returns assigned task points by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Number}
     */
    // TODO: add support for other task types, now Multiple choice only
    UserAnswers.prototype.getTaskPoints = function(ID) {
        var entity = training.getEntity(ID),
            answers = entity.answers,
            userAnswers = this.getUserAnswer(ID).answers,
            userAnswer = (Array.isArray(userAnswers) && userAnswers.length) ? userAnswers[0] : {},
            userAnswerID = userAnswer.answerId || '',
            i = answers.length,
            points = 0;

        while(i--) {
            if (answers[i].hasOwnProperty('points')) {
                if (userAnswerID === answers[i].id) {
                    points += answers[i].points;
                }
            }
        }
        return points;
    };

    /**
     * Checks if task is correct by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isTaskCorrect = function(ID) {
        var entity = training.getEntity(ID),
            userAnswer = this.getUserAnswer(ID);

        return (!entity || !userAnswer) ? false : this.isEntityCorrect(entity);
    };

    /**
     * Returns amount of Quiz task pages answered/ content pages visited.
     * @param {Array} children - Quiz game children
     * @returns {Number} amount of Quiz pages answered
     */
    UserAnswers.prototype.getTotalQuizPageAnswers = function(arr) {
        var ID,
            total = 0;

        for (var i = 0; i < arr.length; i++) {
            ID = arr[i].contentEntityId;

            if ((helpers.entity.isTaskPage(arr[i]) && this.isTaskPageAnswered(ID)) || (helpers.entity.isContentPage(arr[i]) && this.isPageVisited(ID))) {
                total++;
            }
        }

        return total;
    };

    /**
     * Sets computed Quiz map
     * @param {Object} userAnswer - Quiz page user answer
     * @param {Array} children - Quiz children
     */
    UserAnswers.prototype.setComputedQuizMap = function(userAnswer, children) {
        var arr = [];

        for (var i = 0; i < children.length; i++) {
            arr.push(children[i].contentEntityId);
        }

        if (arr.length) {
            userAnswer.computedQuizMap = arr;
        }
    }

    /**
     * Resets Quiz computed map
     * @param {String} ID - Quiz entity ID
     */
    UserAnswers.prototype.resetComputedQuizMap = function(ID) {
        var userAnswer = this.getUserAnswer(ID);

        if (userAnswer && Array.isArray(userAnswer.computedQuizMap)) {
            userAnswer.computedQuizMap = null;
        }
    }

    UserAnswers.prototype.setParser = function(fn) {
        this.parser = fn;
    }

    UserAnswers.prototype.getComputedQuizMap = function(children, shuffleTasks, taskCountToShow) {
        var _this = this,
            sub = this.parser(children);

        var arr = sub.filter(function(o) {
            return _this.isTaskPageAnswered(o.contentEntityId);
        });

        if (!arr.length) {
            arr = (shuffleTasks) ? sub.shuffle() : sub;
            arr = (taskCountToShow) ? arr.splice(0, taskCountToShow) : arr.slice(0);
            return arr;
        }

        var res = arr.reduce(function(a, o) {
            var answer = _this.getUserAnswer(o.contentEntityId);
            a.push({ ID: o.contentEntityId, date: answer.answers[0].answerDate });
            return a;
        }, []).sort(function(a, b) {
            if (a.date < b.date) { return 1; }
            if (a.date > b.date) { return -1; }
            return 0;
        });

        var answers = res.reduce(function(a, o) {
            a.push(training.getEntity(o.ID));
            return a;
        }, []);

        if (answers.length === sub.length || taskCountToShow && answers.length >= taskCountToShow) {
            return (taskCountToShow) ? answers.splice(0, taskCountToShow).reverse() : answers.slice(0).reverse();
        }

        answers.reverse();

        var non = sub.filter(function(o) {
            return !_this.isTaskPageAnswered(o.contentEntityId);
        });


        if (shuffleTasks) {
            non.shuffle();
        }

        if (taskCountToShow) {
            non = non.splice(0, (taskCountToShow - answers.length))
        }

        res = answers.concat(non);

        return res;
    }

    /**
     * Returns Quiz page children (spliced if required).
     * @param {Array} entity - Quiz entity
     * @returns {Array} Quiz page children
     */
    UserAnswers.prototype.getQuizPageChildren = function(options) {
        options = options || {};

        var arr = [],
            entity,
            children = options.children || [],
            computedQuizMap = options.computedQuizMap || null,
            shuffleTasks = options.shuffleTasks,
            taskCountToShow = options.taskCountToShow || 0;

        if (!computedQuizMap.length) {

            arr = this.getComputedQuizMap(children, shuffleTasks, taskCountToShow);

            if (arr.length) {
                return arr;
            }
        }

        for (var i = 0; i < computedQuizMap.length; i++) {
            entity = training.getEntity(computedQuizMap[i]);
            if (entity) {
                arr.push(entity);
            }
        }

        return (taskCountToShow) ? arr.splice(0, taskCountToShow) : arr.slice(0);
    };

    UserAnswers.prototype.getQuizPageProgressDetails = function(options) {
        options = options || {};

        var ID = '',
            children = options.children || [],
            completeDespiteResults = options.completeDespiteResults || false,
            passAlways = options.passAlways || false,
            passThreshold = (options.passThreshold > 100) ? 100 : Math.round(options.passThreshold),
            isAnswered = options.isAnswered || false,
            isVisited = options.isVisited || false,
            inProgress = false,
            totalCorrect = 0,
            userScore = 0,
            status = '',
            correct = false,
            res = {
                ID: options.ID || '',
                childrenID : [],
                progress : [],
                correct : []
            };

        for (var i = 0; i < children.length; i++) {
            ID = children[i].contentEntityId;
            status = 'not-visited';
            correct = false;

            res.childrenID.push(ID);

            if (training.entityID === ID) {
                inProgress = true;
            }

            if (this.isPageVisited(ID)) {
                status = 'visited';
                inProgress = true;
            }

            if (this.isTaskPageAnswered(ID)) {

                if (this.isEntityCorrect(children[i])) {
                    status = 'correct';
                    correct = true;
                    totalCorrect++;
                } else {
                    status = 'incorrect';
                }

                if (!children[i].contentEntity.hasCorrectAnswers) {
                    status = 'visited';
                }

                inProgress = true;
            }

            res.progress.push(status);
            res.correct.push(correct);
        }

        userScore = (children.length) ? Math.round((totalCorrect/children.length) * 100) : 0;

        res.isAnswered = isAnswered;
        res.isPassed = ((userScore >= passThreshold) || passAlways || completeDespiteResults) ? true : false;
        res.inProgress = (isAnswered) ? false : inProgress;
        res.isVisited = isVisited;
        res.userScore = userScore;

        return res;
    };

    UserAnswers.prototype.getQuizProgress = function(ID) {
        if (!ID) {
            return;
        }

        // debugger;

        var entity = training.getEntity(ID);

        if (helpers.entity.isQuizPage(entity)) {
            return this.getQuizPageProgress(ID);
        }

        if (helpers.entity.isQuizTaskPage(ID, training.contentEntities)) {
            return this.getQuizProgress(entity.contentEntity.parentId);
        }

        return;
    }

    /**
     * Returns Quiz page progress by entity ID.
     * @param {String} ID - Entity ID
     * @returns {QuizPageProgress} Quiz page progress
     */
    UserAnswers.prototype.getQuizPageProgress = function(ID) {
        var entity = training.getEntity(ID),
            userAnswer = this.getUserAnswer(ID),
            children,
            totalAnswers;

        if (!entity || !userAnswer || !helpers.entity.isQuizPage(entity)) {
            return;
        }

        children = this.getQuizPageChildren({
            children: (Array.isArray(entity.children)) ? entity.children.slice(0) : [],
            computedQuizMap: (Array.isArray(userAnswer.computedQuizMap)) ? userAnswer.computedQuizMap : [],
            shuffleTasks: !!entity.contentEntity.shuffleTasks,
            taskCountToShow: (Number.isInteger(entity.contentEntity.taskCountToShow)) ? entity.contentEntity.taskCountToShow : 0
        });

        if (!children) {
            return;
        }

        if (!userAnswer.computedQuizMap) {
            this.setComputedQuizMap(userAnswer, children);
        }

        totalAnswers = this.getTotalQuizPageAnswers(children);

        return this.getQuizPageProgressDetails({
            ID: ID,
            children: children,
            completeDespiteResults: (entity.contentEntity && entity.contentEntity.completeDespiteResults),
            passAlways: (entity.contentEntity && entity.contentEntity.passAlways),
            passThreshold: (entity.contentEntity && entity.contentEntity.passThreshold) ? entity.contentEntity.passThreshold : 100,
            isAnswered: (children.length === totalAnswers) ? true : false,
            isVisited: this.isPageVisited(ID)
        });
    };

    /**
     * Checks if Quiz page is answered by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean} true/ false
     */
    UserAnswers.prototype.isQuizPageAnswered = function(ID) {
        var progress = this.getQuizPageProgress(ID);

        return (progress && (progress.isAnswered || progress.isVisited)) ? true : false;
    };

    /**
     * Checks if Quiz page is passed by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isQuizPagePassed = function(ID) {
        var progress = this.getQuizPageProgress(ID);

        return (progress && progress.isPassed) ? true : false;
    };

    /**
     * Checks if Quiz Game page is passed by entity ID.
     * @param {String} ID - Entity ID
     * @returns {Boolean}
     */
    UserAnswers.prototype.isQuizGamePagePassed = function(ID) {
        var progress,
            childEntity,
            childEntityID = '',
            entity = training.getEntity(ID),
            children = (entity && Array.isArray(entity.children)) ? entity.children : [],
            i = children.length;

        while(i--) {
            childEntity = children[i];
            childEntityID = childEntity.contentEntityId;

            if (helpers.entity.isContentPage(childEntity) && !this.isPageVisited(childEntityID)) {
                return false;
            }

            if (helpers.entity.isQuizPage(childEntity)) {
                progress = this.getQuizPageProgress(childEntityID);

                if (!progress || !progress.isPassed && !progress.isVisited) {
                    return false;
                }
            }
        }

        return true;
    };

    /**
     * Returns Entity score by entity ID.
     * @param {String} ID - Entity ID
     * @param {Boolean} [isCorrect=true] - Enable Entity check if is it correct
     * @param {Boolean} [inScore=true] - Enable Entity check if is it in score
     * @returns {Integer}
     */
    UserAnswers.prototype.getEntityScore = function(ID, isCorrect, inScore) {
        var entity = training.getEntity(ID),
            userAnswer = this.getUserAnswer(ID);

        isCorrect = (isCorrect === false) ? false : true;
        inScore = (inScore === false) ? false : true;

        if ((!entity || !userAnswer)) {
            return 0;
        }

        if (helpers.entity.isContentPage(entity)) {
            return 0;
        }

        if (!this.isTaskPageAnswered(ID)) {
            return 0;
        }

        if (inScore && !helpers.entity.isTaskInScore(entity)) {
            return 0;
        }

        if (isCorrect && !this.isTaskCorrect(ID)) {
            return 0;
        }

        return getEntityScore(entity, userAnswer.answers);
    };

    /**
     * Returns Quiz page score by Quiz page ID.
     * @param {String} ID - Quiz page ID
     * @param {Boolean} [isCorrect=true] - Enable Entity check if is it correct
     * @param {Boolean} [inScore=true] - Enable Entity check if is it in score
     * @returns {Integer}
     */
    UserAnswers.prototype.getQuizPageScore = function(ID, isCorrect, inScore) {
        var entity = training.getEntity(ID),
            arr,
            total = 0,
            progress;

        isCorrect = (isCorrect === false) ? false : true;
        inScore = (inScore === false) ? false : true;

        if (!entity || !helpers.entity.isQuizPage(entity)) {
            return 0;
        }

        progress = this.getQuizPageProgress(ID);

        arr = progress.childrenID || [];

        for(var i = 0; i < arr.length; i++) {
            total += this.getEntityScore(arr[i], isCorrect, inScore);
        }

        return total;
    };

    /**
     * Returns Quiz page points by Quiz page ID (use questions given to user).
     * @param {String} ID - Quiz page ID
     * @param {Boolean} [inScore=true] - Enable Entity check if is it in score
     * @returns {Integer}
     */
    UserAnswers.prototype.getQuizPagePoints = function(ID, inScore) {
        var entity = training.getEntity(ID),
            arr,
            total = 0,
            progress;

        inScore = (inScore === false) ? false : true;

        if (!entity || !helpers.entity.isQuizPage(entity)) {
            return 0;
        }

        progress = this.getQuizPageProgress(ID);

        arr = progress.childrenID || [];

        for(var i = 0; i < arr.length; i++) {
            total += training.getEntityPoints(arr[i], inScore);
        }

        return total;
    };

    /**
     * Checks if Course if fully answered.
     * @returns {Boolean}
     */
    UserAnswers.prototype.isCourseVisited = function() {
        var i = training.trainingMap.length,
            ID = '',
            entity;

        while(i--) {
            ID = training.trainingMap[i];
            entity = training.getEntity(ID);



            if (helpers.entity.isQuizPage(entity) && !this.isQuizPageAnswered(ID) || helpers.entity.isContentPage(entity) && !this.isPageVisited(ID) && entity.contentEntity.affectsProgress || helpers.entity.isTaskPage(entity) && (training.sequence || entity.contentEntity.requiresAnswer) && !userAnswers.isTaskPageAnswered(ID)) {
                return false;
            }
        }

        return true;
    };

    /**
     * Resets user answer by ID.
     * @param {String} ID - Task Page ID
     */
    UserAnswers.prototype.resetAnswer = function(ID) {
        var answer = this.getUserAnswer(ID);

        if (answer.answers) {
            answer.answers = [];
            answer.visited = false;
        }
    };

    /**
     * Resets Quiz Game page user answers by ID.
     * @param {Array} ID - Quiz Game Page ID
     */
    UserAnswers.prototype.resetQuizGameAnswers = function(ID) {
        var entity = training.getEntity(ID),
            arr = (entity && entity.children) ? entity.children : [];

        for (var i = 0; i < arr.length; i++) {
            if (arr[i].typeId === 19) {
                this.resetQuizAnswers(arr[i].contentEntityId);
            }
        }
    };

    /**
     * Resets Quiz page user answers by ID.
     * @param {Array} ID - Quiz Page ID
     */
    UserAnswers.prototype.resetQuizAnswers = function(ID) {
        var entity = training.getEntity(ID),
            arr = (entity && entity.children) ? flattenChildren(entity.children) : [];

        // this.resetQuizAnswer(ID);

        for (var i = 0; i < arr.length; i++) {
            this.resetAnswer(arr[i].contentEntityId);
        }
    };

    /**
     * Returns Task answers IDs.
     * @param {String} ID - Task ID
     * @returns {Array}
     */
    UserAnswers.prototype.getTaskAnswersID = function(ID) {
        var res = [],
            answer = this.getUserAnswer(ID),
            answers = answer.answers || [];

        for(var i = 0; i < answers.length; i++) {
            res.push(answers[i].answerId);
        }

        return res;
    };

    /**
     * Returns User course score.
     * @returns {Integer}
     */
    UserAnswers.prototype.getScore = function(score, lastQuizPageID) {
        var progress;

        if (lastQuizPageID) {

            if (score === 100) {
                return score;
            }

            progress = this.getQuizPageProgress(lastQuizPageID);

            if (!progress || !progress.isAnswered) {
                return score;
            }

            return progress.userScore;
        }

        return score;
    };

    /**
     * Returns User course status.
     * @returns {Integer}
     */
    // http://api.gimletlms.com/1.0/Help/ResourceModel?modelName=TrainingProgressStatus%20
    UserAnswers.prototype.getStatus = function(status, lastQuizPageID) {
        var progress;

        if (status === -1) {
            status = 4;
        }

        if (lastQuizPageID) {
            if (status === 1) {
                return status;
            }

            progress = this.getQuizPageProgress(lastQuizPageID);

            if (!progress || !progress.isAnswered) {
                return status;
            }

            return (progress.isPassed || progress.isVisited) ? 1 : 3;

        } else if (this.isCourseVisited()) {
            status = 2;
        }

        return status;
    };

    /**
     * Returns Page data for lesson progress.
     * @returns {Object}
     */
    UserAnswers.prototype.mapPageLessons = function(ID) {
        var res = [],
            answer = this.getUserAnswer(ID),
            answers = answer.answers || [],
            containsQuestionID = function(answer) {
                return !!answer.questionId;
            };

        res = answers.filter(containsQuestionID).map(function(answer) {
            return {
                answerId: answer.answerId || '',
                isPublic: answer.isPublic || false,
                text: answer.answerText || '',
                questionId: answer.questionId
            }
        });

        return {
            lessonId: ID,
            answers: res
        }
    };

    /**
     * Returns Quiz page and it's children data for lesson progress.
     * @returns {Array}
     */
    UserAnswers.prototype.mapQuizLessons = function(ID) {
        var res = [],
            entity = training.getEntity(ID),
            arr = (entity && entity.children) ? flattenChildren(entity.children) : [];

        for (var i = 0; i < arr.length; i++) {
            res.push(this.mapPageLessons(arr[i].contentEntityId));
        }

        return res;
    };

    /**
     * Returns Course data for lesson progress.
     * @returns {Array}
     */
    UserAnswers.prototype.mapCourseLessons = function() {
        var res = [],
            arr  = this.answers.filter(function(answer) {
                return answer.visited;
            });

        for (var i = 0; i < arr.length; i++) {
            res.push(this.mapPageLessons(arr[i].contentEntityId));
        }

        return res;
    };

    /**
     * Returns navigate target id by entity ID.
     * @param {String} ID - Entity ID
     * @returns {String}
     */
    UserAnswers.prototype.getTargetID = function(ID) {
        var res = null,
            answers = this.getUserAnswers(ID),
            entity = training.getEntity(ID);

        if (!entity) {
            return res;
        }

        answers.forEach(function(uAnswer) {
            entity.answers.forEach(function(answer) {
                if (answer.id === uAnswer.answerId) {
                    res = answer.naviTargetId;
                }
            });
        });

        return res;
    };

    /**
     * Returns tasks and answers report.
     * @returns {Array}
     */
    UserAnswers.prototype.getTasksAndAnswers = function() {
        var arr = training.trainingMap.map(function(ID) {
            return {
                entity: training.getEntity(ID),
                answers: userAnswers.getUserAnswers(ID),
            };
        });

        return getTasksAnswers.default(arr);
    };

    /**
     * Returns user answer properties
     * @param {String} ID - Entity ID
     * @param {String} prop - Property to return
     * @param {String} keyword - Match criteria keyword
     * @param {String} value - Match criteria value
     * @returns {Array}
     */
    UserAnswers.prototype.getAnswerProp = function(ID, prop, key, value) {
        return getAnswerProp.default(userAnswers.getUserAnswers(ID), prop, key, value);
    };

    return function(options) {

        if (!userAnswers) {
            training = new Training();

            userAnswers = new UserAnswers(options);
        }

        return userAnswers;
    };
});

